import { useEffect, useRef, useState } from "react";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Building2, Loader2, Upload, Save, Plus } from "lucide-react";

export default function BrandSettings() {
  const { user, currentBusiness, businessId, switchBusiness } = useAuth();
  const isMaster = user && !user.business_id;
  const [form, setForm] = useState({ name: "", brand_color: "#0A84FF", credit_text: "", phone: "", website: "", whatsapp: "", address: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoBust, setLogoBust] = useState(Date.now());
  const [hasLogo, setHasLogo] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const fileRef = useRef();

  const createClient = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/businesses", { name: newName.trim() });
      toast.success(`Added client "${data.name}"`);
      switchBusiness(data.id); // reloads into the new client
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add client");
      setCreating(false);
    }
  };

  useEffect(() => {
    if (currentBusiness) {
      setForm({
        name: currentBusiness.name || "",
        brand_color: currentBusiness.brand_color || "#0A84FF",
        credit_text: currentBusiness.credit_text || "",
        phone: currentBusiness.phone || "",
        website: currentBusiness.website || "",
        whatsapp: currentBusiness.whatsapp || "",
        address: currentBusiness.address || "",
      });
      setHasLogo(!!currentBusiness.logo_storage_path);
    }
  }, [currentBusiness]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/businesses/${businessId}`, form);
      toast.success("Brand saved");
    } catch { toast.error("Could not save"); }
    setSaving(false);
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/businesses/${businessId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHasLogo(true);
      setLogoBust(Date.now());
      toast.success("Logo updated — it now brands this business's slides & videos");
    } catch { toast.error("Logo upload failed"); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const logoUrl = `${API}/businesses/${businessId}/logo?t=${logoBust}`;

  return (
    <div className="p-4 md:p-8 pb-24 md:pb-8 max-w-2xl">
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-bold tracking-tight flex items-center gap-2">
            <Building2 className="w-6 h-6 text-primary" /> Brand Settings
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Logo, colour and contact for <b>{currentBusiness?.name}</b>. These brand this business's AI slides, clips &amp; videos.
          </p>
        </div>
        {isMaster && (
          <Button variant="secondary" onClick={() => { setNewName(""); setNewOpen(true); }} data-testid="add-client-btn" className="flex-shrink-0">
            <Plus className="w-4 h-4 mr-1.5" /> Add client
          </Button>
        )}
      </div>

      <Dialog open={newOpen} onOpenChange={(o) => !creating && setNewOpen(o)}>
        <DialogContent className="max-w-sm" data-testid="add-client-dialog">
          <DialogHeader><DialogTitle>Add a new client</DialogTitle></DialogHeader>
          <div>
            <Label htmlFor="new-client-name">Client / business name</Label>
            <Input id="new-client-name" value={newName} onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Riverside Dental" className="mt-1" data-testid="add-client-name-input"
              onKeyDown={(e) => e.key === "Enter" && createClient()} autoFocus />
            <p className="text-[11px] text-muted-foreground mt-1.5">Creates an empty workspace (media, TVs, playlists, brand) just for this client. You can set its logo &amp; contact next.</p>
          </div>
          <DialogFooter>
            <Button onClick={createClient} disabled={creating || !newName.trim()} data-testid="add-client-save-btn">
              {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : "Create client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="space-y-5">
        <div className="rounded-xl border border-border bg-card p-4">
          <Label>Logo</Label>
          <div className="flex items-center gap-4 mt-2">
            <div className="w-28 h-20 rounded-lg border border-border bg-black/40 flex items-center justify-center overflow-hidden" data-testid="brand-logo-preview">
              {hasLogo ? (
                <img src={logoUrl} alt="logo" className="w-full h-full object-contain" />
              ) : (
                <span className="text-xs text-muted-foreground">No logo</span>
              )}
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={onLogo} data-testid="brand-logo-input" />
              <Button variant="secondary" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="brand-logo-upload-btn">
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4 mr-2" /> Upload logo</>}
              </Button>
              <p className="text-[11px] text-muted-foreground mt-1.5">Transparent PNG works best.</p>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Business name</Label>
            <Input value={form.name} onChange={upd("name")} className="mt-1" data-testid="brand-name-input" />
          </div>
          <div>
            <Label>Brand colour</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={form.brand_color} onChange={upd("brand_color")} className="h-10 w-14 rounded-md bg-transparent border border-border" data-testid="brand-color-input" />
              <Input value={form.brand_color} onChange={upd("brand_color")} className="flex-1" />
            </div>
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.phone} onChange={upd("phone")} className="mt-1" placeholder="(847) 555-0100" data-testid="brand-phone-input" />
          </div>
          <div>
            <Label>Website</Label>
            <Input value={form.website} onChange={upd("website")} className="mt-1" placeholder="avstone.com" data-testid="brand-website-input" />
          </div>
          <div>
            <Label>WhatsApp (for Weekly Ad flyer)</Label>
            <Input value={form.whatsapp} onChange={upd("whatsapp")} className="mt-1" placeholder="+1 847-652-0682" data-testid="brand-whatsapp-input" />
          </div>
          <div>
            <Label>Address (for Weekly Ad flyer)</Label>
            <Input value={form.address} onChange={upd("address")} className="mt-1" placeholder="4817 Oakton St, Skokie, IL" data-testid="brand-address-input" />
          </div>
          <div className="sm:col-span-2">
            <Label>Credit line (shown on slides/videos)</Label>
            <Input value={form.credit_text} onChange={upd("credit_text")} className="mt-1" placeholder="AV Stone • Stone • Cabinets • Design" data-testid="brand-credit-input" />
            <p className="text-[11px] text-muted-foreground mt-1">Leave blank to keep the default credit.</p>
          </div>
        </div>

        <Button onClick={save} disabled={saving} data-testid="brand-save-btn">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save brand</>}
        </Button>
      </div>
    </div>
  );
}
